import React from 'react';
import CustomDialog from 'components/CustomDialog/CustomDialog';
import classNames from 'classnames';
import Button from 'components/CustomButtons/Button';
import styles from 'assets/jss/material-dashboard-pro-react/views/services/FixedBusinessBroadband/sendDialogStyle';
import { makeStyles } from '@material-ui/core';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import Check from '@material-ui/icons/CheckCircleOutlineOutlined';
import { dynamicTranslate, translate } from 'components/Translation';
import { CaptionBold, HeadingM } from 'components/Typography/Typography';
import CustomLinearProgress from 'components/CustomLinearProgress/CustomLinearProgress';
import { useSelector } from 'react-redux';
import { getFiberProLabels } from 'redux/modules/dynamicLabels/selectors';

// @ts-ignore
const useStyles = makeStyles(styles);

interface ISendDialogProps {
	sendRequest: (int: number) => void;
	lines: Array<{
		line: React.ReactElement;
		status: string | 'success' | 'failure' | 'idle' | 'progress';
		requestId?: string;
	}>;
	hideDialog: () => void;
	requestId?: string[];
}

const SendDialog = (props: ISendDialogProps) => {
	const { lines, sendRequest, hideDialog, requestId = false } = props;
	const fiberProLabels = useSelector(getFiberProLabels);
	const classes = useStyles();
	return (
		<CustomDialog
			hideDialog={hideDialog}
			isOpen
			header={fiberProLabels.sendingReq}
			dialogBody={
				<div className={classes.container}>
					{lines.map((line, ind) => (
						<div key={ind} className={classes.line}>
							{line.line}
							<ConditionalRender
								show={line.status === 'success'}
								onTrue={() => (
									<>
										<div
											className={classNames(classes.flex, {
												[classes.lineWithoutNumber]: lines.length === 1,
											})}
										>
											<Check className={classes.success} />
											<CaptionBold disableMargin>
												{fiberProLabels.reqSubmitted}
											</CaptionBold>
										</div>
										<ConditionalRender
											show={!!requestId || !!line.requestId}
											onTrue={() => (
												<div className={classNames(classes.request)}>
													<HeadingM className={classNames(classes.center)} disableMargin>
														{fiberProLabels.successText}
													</HeadingM>
													<HeadingM customClassName={classes.requestId} className={classNames(classes.center)}>
														{dynamicTranslate(
															fiberProLabels.requestId,
															String(requestId || line.requestId)
														)}
													</HeadingM>
												</div>
											)}
										/>
									</>
								)}
							/>
							<ConditionalRender
								show={line.status === 'failure'}
								onTrue={() => (
									<div
										className={classNames(classes.failureContainer, {
											[classes.lineWithoutNumber]: lines.length === 1,
										})}
									>
										<div className={classes.flex}>
											<HighlightOffIcon className={classes.failure} />
											<CaptionBold disableMargin>
												{fiberProLabels.reqFailed}
											</CaptionBold>
										</div>
										<div className={classNames(classes.requestId)}>
											<HeadingM className={classNames(classes.center)} disableMargin>
												{fiberProLabels.failureText}
											</HeadingM>
										</div>
										<div className={classes.buttonContainer}>
											<Button color="primary-light" onClick={() => sendRequest(ind)}>
												{fiberProLabels.retry}
											</Button>
										</div>
									</div>
								)}
							/>
							<ConditionalRender
								show={line.status === 'progress'}
								onTrue={() => (
									<div
										className={classNames({
											[classes.lineWithoutNumber]: lines.length === 1,
										})}
									>
										<CustomLinearProgress color="primary" variant="indeterminate" />
									</div>
								)}
							/>
						</div>
					))}
				</div>
			}
		/>
	);
};

export default SendDialog;
